import { yupResolver } from '@hookform/resolvers/yup';
import { Avatar, Button, Chip } from '@mui/material';
import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { ETags, routes, tabs } from 'src/enums';
import { TFileString } from 'src/global';
import {
  useGetBillingQuery,
  useLazyConnectBillingQuery,
  useLazyUpBalanceQuery,
} from 'src/redux/services/billing';
import { clientsApi, IClientFull } from 'src/redux/services/clients';
import { useDeleteOrganizationMutation } from 'src/redux/services/organization';
import { useGetMeInfoQuery } from 'src/redux/services/users';
import * as yup from 'yup';
import { getImageURL, getProviderTitleByType, isObjectEmpty } from '../../helpers';
import { ReactComponent as IdIcon } from '../../icons/Id.svg';
import { setApplicationFormChanged } from '../../redux/appSlice';
import { EGetProviderAction, useGetProvidersQuery } from '../../redux/services/provider';
import {
  IRuleWithValidation,
  useGetProfileFieldsQuery,
  useGetSettingsQuery,
} from '../../redux/services/settings';
import { RootState } from '../../redux/store';
import { CustomTypography } from '../custom/CustomTypography';
import { AdvancedSettings } from '../extensions/Settings';
import { EditProviders } from '../Providers/editProvidersPanel/EditProviders';
import { IconWithTooltip } from '../shared/IconWithTooltip';
import styles from './Settings.module.css';
import { SettingsItem } from './SettingsItem';

export const editProfileSchema = (rules: IRuleWithValidation[]): yup.AnyObjectSchema => {
  if (!rules) return yup.object();
  return generateValidationSchema(rules.filter((rule) => rule.field_name !== 'password'));
};

export const generateValidationSchema = (rules: IRuleWithValidation[]) => {
  const schemaFields = rules.reduce((schema, field) => {
    if (!field.active) return schema;

    let fieldValidations = yup.string().nullable().notRequired();

    // Применение всех активных правил валидации для поля
    field.validations.forEach((validation) => {
      if (validation.active) {
        fieldValidations = fieldValidations.test({
          name: `${field.field_name}-regex`,
          message: validation.error,
          test: (value) => {
            if (field.required && (value === null || value === undefined || value === '')) {
              return false; // пропускаем проверку, если поле обязательное и пустое
            }
            // Применяем regex только если поле не пустое
            if (value === null || value === undefined || value === '') {
              return true; // пропускаем проверку, если поле пустое
            }
            const result = new RegExp(validation.regex).test(value);
            return result;
          },
        });
      }
    });

    return {
      ...schema,
      [field.field_name]: fieldValidations,
    };
  }, {});

  return yup.object().shape(schemaFields).required();
};

export enum RegistrationPolicyVariants {
  allowed = 'allowed',
  allowed_autoregistration_only = 'allowed_autoregistration_only',
  disabled = 'disabled',
}

export type TSettingsInputs = {
  avatar: TFileString;
  registration_policy: string;
  allow_everyone_to_create_applications: boolean;
  authorize_only_admins: boolean;
  default_public_profile_claims_oauth: string;
  default_public_profile_claims_gravatar: string;
  phone_provider_enabled: boolean;
};

const schema = yup
  .object({
    registration_policy: yup.string().required('Обязательно поле'),
    allow_everyone_to_create_applications: yup.boolean().required('Обязательно поле'),
    authorize_only_admins: yup.boolean().required('Обязательно поле'),
  })
  .required();

interface INewSettingsProps {
  selectedClient: IClientFull;
}

export const NewSettings: FC<INewSettingsProps> = ({ selectedClient }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const applicationFormChanged = useSelector(
    (state: RootState) => state.app.applicationFormChanged,
  );
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [deleteOrg] = useDeleteOrganizationMutation();
  const [connectBilling] = useLazyConnectBillingQuery();
  const [upBalance] = useLazyUpBalanceQuery();

  const { data: dataSettings } = useGetSettingsQuery();
  const { data: profileFields } = useGetProfileFieldsQuery();
  const { data: providers = [] } = useGetProvidersQuery({
    client_id: selectedClient.client_id,
    query: {
      action: EGetProviderAction.all,
    },
  });
  const { refetch: getUser } = useGetMeInfoQuery();
  const { data: billingInfo, isError: isBillingError } = useGetBillingQuery(
    selectedClient.client_id,
  );

  useEffect(() => {
    getUser();
  }, [profileFields]);

  useEffect(() => {
    return () => {
      dispatch(setApplicationFormChanged(false));
    };
  }, []);

  useEffect(() => {
    if (isBillingError) {
      dispatch(clientsApi.util.invalidateTags([ETags.ClientDetails]));
    }
  }, [isBillingError]);

  const methods = useForm<TSettingsInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      authorize_only_admins: dataSettings?.authorize_only_admins,
      allow_everyone_to_create_applications: dataSettings?.allow_everyone_to_create_applications,
      registration_policy: dataSettings?.registration_policy,
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const {
    resetField,
    formState: { dirtyFields },
  } = methods;

  useEffect(() => {
    if (!dataSettings) return;

    const fieldsToReset = [
      'authorize_only_admins',
      'allow_everyone_to_create_applications',
      'registration_policy',
    ] as const;

    fieldsToReset.forEach((fieldName) => {
      const fieldValue = dataSettings[fieldName];
      if (fieldValue !== undefined) {
        resetField(fieldName, { defaultValue: fieldValue });
      }
    });
  }, [dataSettings]);

  useEffect(() => {
    const isDirty = !isObjectEmpty(dirtyFields);
    if (applicationFormChanged !== isDirty) dispatch(setApplicationFormChanged(isDirty));
  }, [Object.values(dirtyFields)]);

  const handleClickConnect = async () => {
    const res = await connectBilling(selectedClient.client_id).unwrap();
    window.location.href = res.url;
  };

  const handleClickUpBalance = async () => {
    const res = await upBalance(selectedClient.client_id).unwrap();
    window.open(res.url, '_blank');
  };

  return (
    <>
      <div className="wrapper-scroll">
        <div className="content">
          <SettingsItem
            title="Способы входа"
            onClick={() => {
              setProviderModalOpen(true);
            }}
            children={
              <div className={styles.providers}>
                {providers
                  .filter((provider) => provider.is_active)
                  .map((provider) => {
                    return (
                      <div key={provider.id} className={styles.provider}>
                        <Avatar
                          src={
                            provider.avatar instanceof File
                              ? URL.createObjectURL(provider.avatar)
                              : provider.avatar?.startsWith('data:')
                              ? provider.avatar
                              : getImageURL(provider.avatar)
                          }
                          className={styles['provider-icon-wrapper']}
                        >
                          {!provider.avatar && <IdIcon />}
                        </Avatar>
                        <div className={styles['provider-name-wrapper']}>
                          <CustomTypography className={clsx('text-14', styles['provider-name'])}>
                            {provider.name}
                          </CustomTypography>
                          <CustomTypography className={clsx('text-12')} color="grey">
                            {getProviderTitleByType(provider.type)}
                          </CustomTypography>
                        </div>
                        {selectedClient.required_providers_ids.includes(provider.id.toString()) && (
                          <IconWithTooltip
                            iconType="starFilled"
                            customTitle=""
                            buttonSx={{}}
                            description="Для входа в приложение требуется
                                 наличие способа входа в профиле пользователя"
                          />
                        )}
                      </div>
                    );
                  })}
              </div>
            }
          />

          <SettingsItem title="Биллинг">
            <div className={styles.billing}>
              {selectedClient.billing_api_key && (
                <div className={styles.paymentInfo}>
                  <Chip label={`Баланс: ${billingInfo?.balance || 0}`} />
                </div>
              )}

              <div className={styles['connect-buttons']}>
                {selectedClient.billing_api_key ? (
                  <Button variant="custom" onClick={() => handleClickUpBalance()}>
                    Пополнить баланс
                  </Button>
                ) : (
                  <Button variant="custom" onClick={() => handleClickConnect()}>
                    Подключить
                  </Button>
                )}
              </div>
            </div>
          </SettingsItem>

          <AdvancedSettings clientId={selectedClient.client_id} />

          <SettingsItem title="Другие действия">
            <Button
              variant="custom2"
              className={styles['margin-bottom']}
              onClick={async () => {
                deleteOrg({}).unwrap();
                navigate(`/${routes.profile}/${tabs.profile}`);
              }}
            >
              Удалить организацию
            </Button>
          </SettingsItem>

          <EditProviders isOpen={providerModalOpen} onClose={() => setProviderModalOpen(false)} />
        </div>
      </div>
    </>
  );
};
